<template>
  <v-row>
    <v-col>
      <ReportHeading
        :label="label"
        :loading="loading"
        @update-report="getPaymentsByManagers"
      >
        <template v-slot:filters>
          <v-col
            cols="4"
            class="py-0 pl-0 pb-3"
          >
            <DateRangePicker
              :start.sync="dateFrom"
              :end.sync="dateTo"
              type="month"
              :filled="false"
              @change="getPaymentsByManagers"
            />
          </v-col>
        </template>
      </ReportHeading>
      <v-card class="mt-2">
        <v-row>
          <v-col>
            <LineChart
              :height="180"
              :chart-data="managersChartData"
              :options="chartOptions"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <LineChart
              :height="180"
              :chart-data="totalChartData"
              :options="chartOptions"
            />
          </v-col>
        </v-row>
      </v-card>
    </v-col>
  </v-row>
</template>

<script>
import DateRangePicker from "@/components/story/DateRangePicker.vue";
import LineChart from "./Chart.js";
import * as managersPaymentApi from "@/backend/managersPaymentsApi.js";
import chartOptions from "./chartOptions.js";
import ReportHeading from "@/components/ReportHeading.vue";
import moment from "moment";
moment.locale("uk");

export default {
  components: {
    LineChart,
    DateRangePicker,
    ReportHeading,
  },
  data: () => ({
    year: null,
    dates: null,
    dateFrom: null,
    dateTo: null,
    dateFormat: "YYYY-MM-DD",
    chartOptions,
    paymentsByManagers: [],
    loading: false,
  }),
  computed: {
    monthsCount() {
      return this.moment(this.dateTo).diff(this.moment(this.dateFrom), "months") + 1;
    },
    labels() {
      const months = [];
      for (let i = 0; i < this.monthsCount; i++) {
        const month = this.moment(this.dateFrom)
          .add(i, "month")
          .format("MMMM YYYY");
        months.push(month);
      }
      return months;
    },
    colors() {
      return [
        "indigo",
        "green",
        "teal",
        "orange",
        "blue",
        "purple",
        "brown",
        "red",
        "#B84A62",
        "pink",
      ];
    },
    managersChartData() {
      return {
        labels: this.labels,
        datasets: (() => {
          if (!this.paymentsByManagers.length) {
            return [];
          }

          let datasets = [];

          this.paymentsByManagers.forEach((p) => {
            const managerTitle = p.manager
              ? p.manager.title
              : "Не вказано Менеджера";
            let managerDataset = datasets.find((d) => d.label === managerTitle);
            if (!managerDataset) {
              managerDataset = {
                label: managerTitle,
                fill: false,
                data: Array(this.monthsCount).fill(0),
                meta: {
                  total: 0,
                  percentage: 0,
                },
              };
              datasets.push(managerDataset);
            }

            const monthIndex = this.moment(p.month).diff(
              this.moment(this.dateFrom),
              "months"
            );

            managerDataset.data[monthIndex] += p.amount;
            managerDataset.meta.total += p.amount;
          });

          if (datasets.length) {
            const total = datasets.reduce((acc, el) => {
              return acc + el.meta.total;
            }, 0);

            if (total > 0) {
              datasets.forEach((d) => {
                d.meta.percentage = (d.meta.total / total) * 100;
              });
            }

            datasets = datasets
              .filter((d) => d.meta.total > 0)
              .sort((a, b) => {
                return b.meta.total - a.meta.total;
              });

            for (let i = 0; i < datasets.length; i++) {
              datasets[i].backgroundColor = this.colors[i];
              datasets[i].borderColor = this.colors[i];
            }
          }
          return datasets;
        })(),
      };
    },
    totalChartData() {
      return {
        labels: this.labels,
        datasets: (() => {
          if (!this.paymentsByManagers.length) {
            return [];
          }

          const totalDataset = {
            label: "Загалом",
            fill: false,
            data: Array(this.monthsCount).fill(0),
            meta: {
              total: 0,
            },
            backgroundColor: "grey",
            borderDash: [15],
            lineTension: 0,
          };
          const servicesDataset = {
            label: "Послуги",
            fill: false,
            data: Array(this.monthsCount).fill(0),
            meta: {
              total: 0,
              percentage: 0,
            },
            borderDash: [15],
            lineTension: 0,
            paymentsForPackage: true,
          };
          const productsDataset = {
            label: "Продукти",
            fill: false,
            data: Array(this.monthsCount).fill(0),
            meta: {
              total: 0,
              percentage: 0,
            },
            borderDash: [15],
            lineTension: 0,
            paymentsForPackage: false,
          };

          let datasets = [totalDataset, servicesDataset, productsDataset];

          this.paymentsByManagers.forEach((p) => {
            const monthIndex = this.moment(p.month).diff(
              this.moment(this.dateFrom),
              "months"
            );

            if (p.paymentsForPackage) {
              servicesDataset.data[monthIndex] += p.amount;
              servicesDataset.meta.total += p.amount;
            } else {
              productsDataset.data[monthIndex] += p.amount;
              productsDataset.meta.total += p.amount;
            }

            totalDataset.data[monthIndex] += p.amount;
            totalDataset.meta.total += p.amount;
          });

          if (datasets.length) {
            const total = totalDataset.meta.total;

            if (total > 0) {
              datasets.forEach((d) => {
                if (d === totalDataset) {
                  return;
                }
                d.meta.percentage = (d.meta.total / total) * 100;
              });
            }

            datasets = datasets
              .filter((d) => d.meta.total > 0)
              .sort((a, b) => {
                return b.meta.total - a.meta.total;
              });

            for (let i = 0; i < datasets.length; i++) {
              datasets[i].backgroundColor = this.colors[i];
              datasets[i].borderColor = this.colors[i];
            }
          }

          return datasets;
        })(),
      };
    },
    label() {
      return `Продажі (по оплатах) з ${moment(this.dateFrom).format(
        "MMMM YYYY"
      )} по
                ${moment(this.dateTo).format("MMMM YYYY")} , тис.грн`;
    },
  },
  created() {
    this.dateFrom = this.moment("2019-01-01").format(this.dateFormat);
    this.dateTo = this.moment().format(this.dateFormat);
    this.getPaymentsByManagers();
  },
  methods: {
    async getPaymentsByManagers() {
      this.loading = true;
      this.paymentsByManagers = [];

      try {
        this.paymentsByManagers = await managersPaymentApi.getManagersPayments(
          this.dateFrom,
          this.dateTo
        );
      } catch {
        this.$notifyWarning(
          "В даних звіту помилки. Подивіться в Система/Стан БД"
        );
      }

      this.loading = false;
    },
  },
};
</script>
